// src/observability/datadog.ts
import type { ErrorInfo } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { reactPlugin, addReactError } from '@datadog/browser-rum-react';
import { rumBeforeSend, logsBeforeSend } from './scrubber';
import { ddConfig } from './config';
import { env } from '@/lib/env';

const isEnabled = ddConfig.enabled;

// Route SDK intake through the local Vite mock only during Vite dev server runs.
// Docker/nginx builds do not serve /dd-mock.
const proxy = import.meta.env.DEV && ddConfig.useMock ? '/dd-mock' : undefined;

// Derive the API base URL from VITE_API_URL (mirrors env.ts). Supports both
// relative same-origin values like /api and absolute values like https://host/api.
function resolveApiBaseUrl(apiUrl: string | undefined): string {
  const url = new URL(apiUrl || '/api', window.location.origin);
  url.pathname = url.pathname.replace(/\/?$/, '/');
  url.search = '';
  url.hash = '';
  return url.href;
}

const apiBaseUrl = resolveApiBaseUrl(env.apiUrl);

function initSdk(): void {
  if (!ddConfig.appId || !ddConfig.clientToken) {
    console.error('[obs] Datadog SDK missing required config: appId and clientToken must be set');
    return;
  }
  try {
    datadogRum.init({
      applicationId: ddConfig.appId,
      clientToken: ddConfig.clientToken,
      site: ddConfig.site,
      service: ddConfig.service,
      env: ddConfig.env,
      version: ddConfig.version,
      proxy,
      sessionSampleRate: 100,
      sessionReplaySampleRate: ddConfig.replaySampleRate,
      defaultPrivacyLevel: 'mask',
      enablePrivacyForActionName: true,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      allowedTracingUrls: [
        // First-party production domains (*.deephealth.*)
        { match: /^https:\/\/[^/]+\.deephealth\.[^/]+\/api\//, propagatorTypes: ['tracecontext', 'datadog'] },
        // Configured API base on any host - covers custom/marketing domains, local dev,
        // and absolute VITE_API_URL deployments.
        { match: (url: string) => url.startsWith(apiBaseUrl), propagatorTypes: ['tracecontext', 'datadog'] },
      ],
      traceSampleRate: ddConfig.traceSampleRate,
      propagateTraceBaggage: true,
      enableExperimentalFeatures: ['feature_flags'],
      beforeSend: rumBeforeSend,
      plugins: [reactPlugin({ router: true })],
    });

    datadogRum.setGlobalContextProperty('tenant_id', ddConfig.tenant);
    datadogRum.setGlobalContextProperty('portal', 'patient');
    datadogRum.setGlobalContextProperty('ris_flavor', ddConfig.risFlavor);

    datadogLogs.init({
      clientToken: ddConfig.clientToken,
      site: ddConfig.site,
      service: ddConfig.service,
      env: ddConfig.env,
      version: ddConfig.version,
      proxy,
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ['warn', 'error'],
      sessionSampleRate: 100,
      beforeSend: logsBeforeSend,
    });
  } catch (err) {
    // Always surface init failures in dev so they are never silently swallowed.
    console.error('[obs] Datadog SDK init failed:', err);
  }
}

if (isEnabled) initSdk();

const toError = (e: unknown) => e instanceof Error ? e : new Error(String(e));

/** FR-05: Set the RUM user context after authentication. */
export function setRumUser(id: string): void {
  if (!isEnabled) return;
  try { datadogRum.setUser({ id }); } catch { /* never interrupt caller */ }
}

/** FR-05: Clear the RUM user context on logout. */
export function clearRumUser(): void {
  if (!isEnabled) return;
  try { datadogRum.clearUser(); } catch { /* never interrupt caller */ }
}

export function setSiteGroupCode(siteGroupCode: string): void {
  if (!isEnabled) return;
  try { datadogRum.setGlobalContextProperty('site_group_code', siteGroupCode); } catch { /* never interrupt caller */ }
}

export const rumRootOptions = {
  // Log the error in all environments for client-side debuggability.
  // info (React component tree) is omitted — it can contain PHI in component props.
  onUncaughtError:    (e: unknown, info: unknown) => { addReactError(toError(e), info as ErrorInfo); console.error('Uncaught error:', e); },
  onCaughtError:      (e: unknown, info: unknown) => { addReactError(toError(e), info as ErrorInfo); console.error('Caught error:', e); },
  onRecoverableError: (e: unknown, info: unknown) => { addReactError(toError(e), info as ErrorInfo); console.warn('Recoverable error:', e); },
} as const;
